import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  message,
  Modal,
  Row,
  Space,
  Spin,
  Tag,
  Timeline,
  TimePicker,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  SaveOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useFirestore } from "react-redux-firebase";
import * as dayjs from "dayjs";
import { DATE_FORMAT_DD_MM_YYYY } from "constants/DateConstant";
import { FIRESTORE_SHIPMENTS_TABLE } from "constants/FirestoreConstant";
import { cancelShipment, updateShipment, getShipmentErrorMessage } from "../../../../functions/api/svz";

const SHIPMENT_STATUS_COLORS = {
  CREATED: "default",
  PICKED_UP: "blue",
  IN_TRANSIT: "orange",
  DELIVERED: "green",
  CANCELLED: "red",
};

const SHIPMENT_STATUS_LABELS = {
  CREATED: "Aangemaakt",
  PICKED_UP: "Opgehaald",
  IN_TRANSIT: "Onderweg",
  DELIVERED: "Geleverd",
  CANCELLED: "Geannuleerd",
};

const ShipmentDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const firestore = useFirestore();
  const { shipmentId, mode } = useParams();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(mode === "edit");

  useEffect(() => {
    loadShipment();
  }, [shipmentId]);

  useEffect(() => {
    if (shipment && isEditMode) {
      form.setFieldsValue({
        recipientName: shipment.recipientName,
        recipientEmail: shipment.recipientEmail,
        recipientPhone: shipment.recipientPhone,
        street: shipment.address?.street,
        houseNumber: shipment.address?.houseNumber,
        addition: shipment.address?.addition,
        zipCode: shipment.address?.zipCode,
        city: shipment.address?.city,
        country: shipment.address?.country || "NL",
        deliveryDate: shipment.deliveryDate ? dayjs(shipment.deliveryDate) : null,
        deliveryTimeFrom: shipment.deliveryTimeFrom ? dayjs(shipment.deliveryTimeFrom, "HH:mm") : null,
        deliveryTimeTill: shipment.deliveryTimeTill ? dayjs(shipment.deliveryTimeTill, "HH:mm") : null,
        notes: shipment.notes,
      });
    }
  }, [shipment, isEditMode]);

  const loadShipment = async () => {
    setLoading(true);
    try {
      const doc = await firestore
        .collection(FIRESTORE_SHIPMENTS_TABLE)
        .doc(shipmentId)
        .get();
      
      if (doc.exists) {
        setShipment({ id: doc.id, ...doc.data() });
      } else {
        messageApi.error(t("shipment.not_found"));
      }
    } catch (error) {
      messageApi.error(t("shipment.load_failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const updateData = {
        recipientName: values.recipientName,
        recipientEmail: values.recipientEmail,
        recipientPhone: values.recipientPhone,
        address: {
          street: values.street,
          houseNumber: values.houseNumber,
          addition: values.addition || null,
          zipCode: values.zipCode,
          city: values.city,
          province: shipment.address?.province || null,
          country: values.country,
        },
        deliveryDate: values.deliveryDate ? values.deliveryDate.format("YYYY-MM-DD") : null,
        deliveryTimeFrom: values.deliveryTimeFrom ? values.deliveryTimeFrom.format("HH:mm") : null,
        deliveryTimeTill: values.deliveryTimeTill ? values.deliveryTimeTill.format("HH:mm") : null,
        notes: values.notes,
      };

      // Via de API, zodat SVZ meegenomen wordt en de statushistorie op één plek bijgehouden
      // wordt. Schrijf niet rechtstreeks naar Firestore.
      const updated = await updateShipment(shipmentId, updateData);

      if (updated?.svzSyncPending) {
        messageApi.warning(t("shipment.svz_not_notified"));
      } else {
        messageApi.success(t("shipment.updated_success"));
      }

      setIsEditMode(false);
      loadShipment();
    } catch (error) {
      messageApi.error(getShipmentErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    Modal.confirm({
      title: t("shipment.cancel_confirm_title"),
      icon: <ExclamationCircleOutlined />,
      content: t("shipment.cancel_confirm_message", { orderId: shipment?.orderId }),
      okText: t("yes"),
      cancelText: t("no"),
      okType: "danger",
      onOk: async () => {
        try {
          // Via de API: die controleert of annuleren nog mag, probeert het bij SVZ en legt
          // de statushistorie vast.
          const result = await cancelShipment(shipmentId);

          if (result?.svzNotified === false) {
            messageApi.warning(t("shipment.svz_not_notified"));
          } else {
            messageApi.success(t("shipment.cancelled_success"));
          }

          navigate("/cms/shipments");
        } catch (error) {
          messageApi.error(getShipmentErrorMessage(error));
        }
      },
    });
  };

  const canEdit = shipment && shipment.status?.current !== "DELIVERED" && shipment.status?.current !== "CANCELLED";

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!shipment) {
    return (
      <Card>
        <p>{t("shipment.not_found")}</p>
      </Card>
    );
  }

  return (
    <>
      {contextHolder}
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/cms/shipments")}>
              {t("back")}
            </Button>
            {!isEditMode && canEdit && (
              <Button type="primary" icon={<EditOutlined />} onClick={() => setIsEditMode(true)}>
                {t("edit")}
              </Button>
            )}
            {canEdit && (
              <Button danger icon={<CloseCircleOutlined />} onClick={handleCancel}>
                {t("shipment.cancel")}
              </Button>
            )}
          </Space>
        </div>

        <Row gutter={24}>
          <Col xs={24} lg={16}>
            <Card title={t("shipment.details")} style={{ marginBottom: 24 }}>
              {!isEditMode ? (
                <Descriptions column={1} bordered>
                  <Descriptions.Item label={t("order.order_id")}>
                    <a onClick={() => navigate(`/cms/orders/all?search=${shipment.orderId}`)}>
                      {shipment.orderId}
                    </a>
                  </Descriptions.Item>
                  <Descriptions.Item label={t("shipment.status")}>
                    <Tag color={SHIPMENT_STATUS_COLORS[shipment.status?.current] || "default"}>
                      {SHIPMENT_STATUS_LABELS[shipment.status?.current] || shipment.status?.current}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label={t("shipment.svz_shipment_id")}>
                    {shipment.svzShipmentId || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("shipment.tracking_number")}>
                    {shipment.trackingNumber || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("shipment.tracking_url")}>
                    {shipment.trackingUrl ? (
                      <a href={shipment.trackingUrl} target="_blank" rel="noopener noreferrer">
                        📦 Track & Trace
                      </a>
                    ) : (
                      "-"
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("shipment.recipient_name")}>
                    {shipment.recipientName}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("email")}>{shipment.recipientEmail}</Descriptions.Item>
                  <Descriptions.Item label={t("phone")}>{shipment.recipientPhone || "-"}</Descriptions.Item>
                  <Descriptions.Item label={t("address")}>
                    {shipment.address?.street} {shipment.address?.houseNumber}
                    <br />
                    {shipment.address?.zipCode} {shipment.address?.city}
                    <br />
                    {shipment.address?.country}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("shipment.weight")}>
                    {shipment.weight ? `${shipment.weight} kg` : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("shipment.delivery_date")}>
                    {shipment.deliveryDate ? dayjs(shipment.deliveryDate).format(DATE_FORMAT_DD_MM_YYYY) : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("shipment.delivery_time")}>
                    {shipment.deliveryTimeFrom && shipment.deliveryTimeTill
                      ? `${shipment.deliveryTimeFrom} - ${shipment.deliveryTimeTill}`
                      : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("shipment.notes")}>{shipment.notes || "-"}</Descriptions.Item>
                </Descriptions>
              ) : (
                <Form form={form} layout="vertical" onFinish={handleSave}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="recipientName"
                        label={t("shipment.recipient_name")}
                        rules={[{ required: true, message: t("form.required") }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="recipientEmail"
                        label={t("email")}
                        rules={[
                          { required: true, message: t("form.required") },
                          { type: "email", message: t("form.enter_valid_email") },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item name="recipientPhone" label={t("phone")}>
                    <Input />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="street"
                        label={t("street")}
                        rules={[{ required: true, message: t("form.required") }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        name="houseNumber"
                        label={t("house_number")}
                        rules={[{ required: true, message: t("form.required") }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="addition" label={t("shipment.addition")}>
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        name="zipCode"
                        label={t("zip_code")}
                        rules={[{ required: true, message: t("form.required") }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="city"
                        label={t("city")}
                        rules={[{ required: true, message: t("form.required") }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item
                        name="country"
                        label={t("country")}
                        rules={[{ required: true, message: t("form.required") }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item name="deliveryDate" label={t("shipment.delivery_date")}>
                        <DatePicker format={DATE_FORMAT_DD_MM_YYYY} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="deliveryTimeFrom" label={t("shipment.delivery_time_from")}>
                        <TimePicker format="HH:mm" style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="deliveryTimeTill" label={t("shipment.delivery_time_till")}>
                        <TimePicker format="HH:mm" style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item name="notes" label={t("shipment.notes")}>
                    <Input.TextArea rows={3} />
                  </Form.Item>

                  <Space>
                    <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
                      {t("save")}
                    </Button>
                    <Button onClick={() => setIsEditMode(false)}>{t("cancel")}</Button>
                  </Space>
                </Form>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card title={t("shipment.history")}>
              <Timeline>
                {/* Show status history */}
                {shipment.status?.history && shipment.status.history.length > 0 ? (
                  shipment.status.history.map((historyItem, index) => (
                    <Timeline.Item 
                      key={index}
                      color={SHIPMENT_STATUS_COLORS[historyItem.to] || "blue"}
                    >
                      <div>
                        <strong>
                          {SHIPMENT_STATUS_LABELS[historyItem.from] || historyItem.from} → {SHIPMENT_STATUS_LABELS[historyItem.to] || historyItem.to}
                        </strong>
                      </div>
                      <div style={{ fontSize: 12, color: "#888" }}>
                        {dayjs.unix(historyItem.at).format("DD-MM-YYYY HH:mm")}
                      </div>
                      {historyItem.source && (
                        <div style={{ fontSize: 12, color: "#888" }}>
                          Bron: {historyItem.source}
                        </div>
                      )}
                    </Timeline.Item>
                  ))
                ) : (
                  <Timeline.Item color="green">
                    {t("shipment.created_at")}: {dayjs(shipment.createdAt).format("DD-MM-YYYY HH:mm")}
                  </Timeline.Item>
                )}
                {shipment.trackingUrl && (
                  <Timeline.Item color="orange">
                    {t("shipment.tracking_available")}
                  </Timeline.Item>
                )}
              </Timeline>
            </Card>
          </Col>
        </Row>
      </Card>
    </>
  );
};

export default ShipmentDetail;
